// Everything the client says to the server, in one place. The shapes mirror the server's DTOs
// (Server/Auth, Server/Portal, Server/Connect, Server/Licensing), which it writes in camelCase.

/** Same-origin by default, which is what the Vite proxy in vite.config.ts sets up. */
export const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '/api'

/**
 * A refusal from the server. `message` is the server's reason when it gave one, meant to be shown
 * as-is; `body` is the whole answer, for the few refusals that carry more than a reason.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function api<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  })
  const text = await response.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }
  if (!response.ok) {
    const reason = (data as { reason?: string } | null)?.reason
    throw new ApiError(response.status, reason ?? defaultMessage(response.status), data)
  }
  return data as T
}

function defaultMessage(status: number): string {
  switch (status) {
    case 401:
      return 'You are not signed in.'
    case 403:
      return 'You are not allowed to do that.'
    case 404:
      return 'Not found.'
    default:
      return `The server answered ${status}.`
  }
}

// ---------------------------------------------------------------------------- who

export interface IdentityInfo {
  id: string
  provider: string
  email: string
  displayName: string
  linkedUtc: string
  lastLoginUtc: string | null
}

export interface PasskeyInfo {
  id: string
  name: string
  createdUtc: string
  lastUsedUtc: string | null
  transports: string[]
}

export interface UserInfo {
  id: string
  name: string
  email: string
  mobile: string
  mobileVerified: boolean
  createdUtc: string
  identities: IdentityInfo[]
  passkeys: PasskeyInfo[]
  displayName: string
}

export interface Providers {
  google: boolean
  microsoft: boolean
  sms: boolean
  passkey: boolean
}

export interface Me {
  user: UserInfo | null
  providers: Providers
}

export interface SignInResponse {
  user: UserInfo
  isNew: boolean
}

export interface CeremonyOptions {
  stateId: string
  options: unknown
}

export type ExternalProvider = 'google' | 'microsoft'

export const auth = {
  me: () => api<Me>('GET', '/auth/me'),
  /** A full navigation, not a fetch: the provider redirects the browser back to the server. */
  externalUrl: (provider: ExternalProvider, returnUrl: string) =>
    `${API_BASE}/auth/external/${provider}?returnUrl=${encodeURIComponent(returnUrl)}`,
  smsSend: (mobile: string) => api<{ mobile: string }>('POST', '/auth/sms/send', { mobile }),
  smsVerify: (mobile: string, code: string) => api<SignInResponse>('POST', '/auth/sms/verify', { mobile, code }),
  passkeyLoginOptions: () => api<CeremonyOptions>('POST', '/auth/passkey/login/options'),
  passkeyLogin: (stateId: string, response: unknown) => api<SignInResponse>('POST', '/auth/passkey/login', { stateId, response }),
  passkeyRegisterOptions: () => api<CeremonyOptions>('POST', '/auth/passkey/register/options'),
  passkeyRegister: (stateId: string, response: unknown, name: string) =>
    api<UserInfo>('POST', '/auth/passkey/register', { stateId, response, name }),
  removePasskey: (id: string) => api<UserInfo>('DELETE', `/auth/passkeys/${id}`),
  unlinkIdentity: (id: string) => api<UserInfo>('DELETE', `/auth/identities/${id}`),
  updateProfile: (name: string, email: string) => api<UserInfo>('PUT', '/auth/profile', { name, email }),
  logout: () => api<void>('POST', '/auth/logout'),
}

// ---------------------------------------------------------------------------- licenses

export interface LimitInfo {
  name: string
  maxValue: number
  unlimited: boolean
}

export interface RateWindow {
  limit: number
  used: number
  left: number | null
}

export interface AccountInfo {
  name: string
  monthlyLimit: number
  usedThisMonth: number
  balanceLeft: number
  minute: RateWindow
  hour: RateWindow
  day: RateWindow
}

export interface LicenseInfo {
  id: string
  name: string
  disabled: boolean
  expired: boolean
  expiresUtc: string | null
  features: string[]
  limits: LimitInfo[]
  accounts: AccountInfo[]
  messageToAllEditors: string
  messageToAllVisitors: string
  stopEdit: boolean
  stopVisit: boolean
  active: boolean
}

export interface TemplateInfo {
  id: string
  name: string
  features: string[]
  limits: LimitInfo[]
  accounts: AccountInfo[]
}

export interface LicenseSummary {
  id: string
  name: string
  disabled: boolean
  expired: boolean
  expiresUtc: string | null
  apiKeys: number
  installations: number
}

export interface ApiKeyInfo {
  id: string
  disabled: boolean
  canExpire: boolean
  expirationUtc: string | null
}

export interface GrantedUserInfo {
  id: string
  name: string
  email: string
  mobile: string
}

export interface InstallationDetail {
  id: string
  installationKey: string
  name: string
  lastContact: string | null
  lastContactIp: string
  serverName: string
  machineName: string
  buildVersion: string
  externalAddresses: string[]
  users: number
  editors: number
  admins: number
  nodes: number
  grantedUsers: GrantedUserInfo[]
  /** Sign-in with Relatude.License works only while this is true and from one of `hosts`. */
  approved: boolean
  approvedUtc: string | null
  hosts: string[]
  /** Addresses seen in sign-in attempts that the owner has not approved. */
  pendingHosts: string[]
}

export interface LicenseDetail {
  info: LicenseInfo
  apiKeys: ApiKeyInfo[]
  installations: InstallationDetail[]
}

export interface AccessInfo {
  installationId: string
  installationName: string
  installationKey: string
  licenseName: string
  ownerName: string
}

export interface CreateLicenseResult {
  licenseId: string
  apiKey: string
}

export const portal = {
  templates: () => api<TemplateInfo[]>('GET', '/portal/templates'),
  licenses: () => api<LicenseSummary[]>('GET', '/portal/licenses'),
  access: () => api<AccessInfo[]>('GET', '/portal/access'),
  createLicense: (templateId: string | null, name: string) =>
    api<CreateLicenseResult>('POST', '/portal/licenses', { templateId, name }),
  license: (id: string) => api<LicenseDetail>('GET', `/portal/licenses/${id}`),
  rename: (id: string, name: string) => api<LicenseDetail>('PUT', `/portal/licenses/${id}`, { name }),
  createApiKey: (id: string) => api<ApiKeyInfo>('POST', `/portal/licenses/${id}/api-keys`),
  updateApiKey: (id: string, keyId: string, disabled: boolean) =>
    api<ApiKeyInfo>('PUT', `/portal/licenses/${id}/api-keys/${keyId}`, { disabled }),
  deleteApiKey: (id: string, keyId: string) => api<void>('DELETE', `/portal/licenses/${id}/api-keys/${keyId}`),
  grant: (id: string, installationId: string, identifier: string) =>
    api<InstallationDetail>('POST', `/portal/licenses/${id}/installations/${installationId}/users`, { identifier }),
  revoke: (id: string, installationId: string, userId: string) =>
    api<InstallationDetail>('DELETE', `/portal/licenses/${id}/installations/${installationId}/users/${userId}`),
  setInstallationApproved: (id: string, installationId: string, approved: boolean) =>
    api<InstallationDetail>('PUT', `/portal/licenses/${id}/installations/${installationId}`, { approved }),
  approveHost: (id: string, installationId: string, host: string) =>
    api<InstallationDetail>('PUT', `/portal/licenses/${id}/installations/${installationId}/hosts`, { host }),
  removeHost: (id: string, installationId: string, host: string) =>
    api<InstallationDetail>('DELETE', `/portal/licenses/${id}/installations/${installationId}/hosts/${encodeURIComponent(host)}`),
  dismissPendingHost: (id: string, installationId: string, host: string) =>
    api<InstallationDetail>('DELETE', `/portal/licenses/${id}/installations/${installationId}/pending-hosts/${encodeURIComponent(host)}`),
}

// ---------------------------------------------------------------------------- connect

export interface LoginRequestInfo {
  requestId: string
  installationName: string
  licenseName: string
  /** The address the browser would be sent back to: host, plus the port when it is not the default. */
  redirectHost: string
  expiresUtc: string
  installationApproved: boolean
  hostApproved: boolean
}

export interface AuthorizeResult {
  redirectTo: string
}

/** The body of a 403 from authorize when the owner may approve the installation or its address and try again. */
export interface ApprovalNeeded {
  needsApproval: true
  reason: string
  host: string
  installationName: string
  installationApproved: boolean
}

export function approvalNeeded(error: unknown): ApprovalNeeded | null {
  if (!(error instanceof ApiError) || error.status !== 403) return null
  const body = error.body as Partial<ApprovalNeeded> | null
  return body?.needsApproval && body.host ? (body as ApprovalNeeded) : null
}

export const connect = {
  request: (id: string) => api<LoginRequestInfo>('GET', `/connect/login-requests/${encodeURIComponent(id)}`),
  /** `approve` is the owner saying yes to an installation or address that is not approved yet, in the same step. */
  authorize: (id: string, approve = false) =>
    api<AuthorizeResult>('POST', `/connect/login-requests/${encodeURIComponent(id)}/authorize`, { approve }),
}

// ---------------------------------------------------------------------------- pairing

/**
 * An installation with no license asking for one. It made the pairing itself with no credentials -
 * it has none yet - and is waiting for somebody to say which license it may have. That somebody is
 * whoever opened the claim url, and it is the only step in the flow that carries any authority.
 */
export interface PairingInfo {
  pairingId: string
  /** what the installation calls itself, when it said */
  installationName: string | null
  installationKey: string
  expiresUtc: string
  /** somebody has already answered it; the installation may not have collected the keys yet */
  completed: boolean
  licenseName: string | null
}

export const pairing = {
  describe: (id: string) => api<PairingInfo>('GET', `/connect/pairings/${encodeURIComponent(id)}`),
  /** Answers the pairing with one of the user's own licenses, or with a new one from a product bundle. */
  complete: (id: string, choice: { licenseId?: string; templateId?: string | null; name?: string }) =>
    api<{ licenseId: string; licenseName: string }>('POST', `/portal/pairings/${encodeURIComponent(id)}`, {
      licenseId: choice.licenseId ?? null,
      templateId: choice.templateId ?? null,
      name: choice.name ?? null,
    }),
}

// ---------------------------------------------------------------------------- helpers

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return String(error)
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}
